// One bounded layout pass per changed label. Never add a permanent root frame
// listener for every keystroke (the bundled EditableLabel does that).
export function configureTextEditor(label) {
  // Numeric steppers also use EditableLabel; only drawing labels need this.
  if (label.get('themeTags', []).includes('numericstepper')) return;
  let pending;
  let panel;
  let parent;
  let disposed = false;
  let offsetX = 0;
  let offsetY = 0;
  const listeners = [];
  const root = label.root;
  const syncText = label._syncText;
  // While typing the DOM owns the draft/caret. Canvas render passes must not
  // write an older label value back into the focused editor (including IME).
  label._syncText = function () {
    if (!this.get('active')) syncText.call(this);
  };
  label.set('editOn', 'none');
  const style = (node, key, value) => {
    if (node.style[key] !== value) node.style[key] = value;
  };
  const set = (target, key, value) => {
    const previous = target.get(key);
    if (typeof value === 'number' && typeof previous === 'number' && Math.abs(previous - value) < 0.01) return;
    if (previous !== value) target.set(key, value);
  };
  const schedule = () => {
    if (pending || disposed || root.isDisposed()) return;
    pending = requestAnimationFrame(() => {
      pending = undefined;
      layout();
    });
  };

  function layout() {
    if (disposed || label.isDisposed()) return;
    if (!panel) {
      let ancestor = label.parent;
      while (ancestor && !ancestor.isType('StockPanel')) ancestor = ancestor.parent;
      panel = ancestor;
      if (panel) {
        listeners.push(panel.plotContainer.events.on('boundschanged', schedule));
        // Equal padding keeps the canvas and HTML wrapping widths identical.
        label.setAll({ oversizedBehavior: 'wrap', breakWords: true,
          paddingLeft: 6, paddingRight: 6, paddingTop: 6, paddingBottom: 6 });
      }
    }
    if (label.parent !== parent) {
      parent = label.parent;
      if (parent) listeners.push(parent.events.on('positionchanged', schedule));
    }
    if (panel) {
      const plot = panel.plotContainer.globalBounds();
      const bounds = label.globalBounds();
      const scale = label.compositeScale() || 1;
      const anchor = label.parent.toGlobal({x: label.x(), y: label.y()});
      // Off-screen data anchors remain off-screen when panning the chart.
      if (anchor.x >= plot.left && anchor.x <= plot.right && anchor.y >= plot.top && anchor.y <= plot.bottom) {
        const left = bounds.left - offsetX * scale;
        const desiredLeft = Math.max(plot.left + 3, Math.min(left, plot.right - 100));
        offsetX = (desiredLeft - left) / scale;
        set(label, 'dx', offsetX);
        set(label, 'maxWidth', Math.max(20, Math.floor((plot.right - 3 - desiredLeft) / scale)));
        set(label, 'maxHeight', Math.max(20, Math.floor((plot.bottom - plot.top - 6) / scale)));
        const top = bounds.top - offsetY * scale;
        offsetY = (Math.max(plot.top + 3, Math.min(top, plot.bottom - 3 - (bounds.bottom - bounds.top))) - top) / scale;
        set(label, 'dy', offsetY);
      }
    }
    syncEditor();
  }

  function syncEditor() {
    if (!label.get('active')) return;
    const input = label.getPrivate('input');
    const textarea = label.getPrivate('textarea');
    const host = input?.getPrivate('htmlElement');
    if (!textarea || !host) return;
    // The HTML editor owns the single editing outline. The canvas background
    // otherwise draws a second, larger border around it.
    const background = label.get('background');
    // Keep the transparent background's hit area: hiding the sprite itself
    // makes subsequent clicks miss the drawing.
    if (background) {
      background.setAll({ forceHidden: false, strokeOpacity: 0, fillOpacity: 0 });
      background.states.lookup('active')?.set('strokeOpacity', 0);
      background.states.lookup('default')?.set('strokeOpacity', 0);
    }
    const text = label._text;
    const bounds = text.localBounds();
    const maxWidth = label.get('maxWidth', Infinity) - label.get('paddingLeft', 0) - label.get('paddingRight', 0);
    // Reserve the latest draft width immediately, before the canvas has had a
    // chance to measure the next frame. Fast keystrokes must not wrap/vanish in
    // a box sized for the previous character.
    const context = root.dom.querySelector('canvas')?.getContext('2d');
    let draftWidth = 0;
    if (context) {
      context.save();
      context.font = `${label.get('fontStyle', 'normal')} ${label.get('fontWeight', 'normal')} ${label.get('fontSize', 13)}${typeof label.get('fontSize', 13) === 'number' ? 'px' : ''} ${label.get('fontFamily', 'sans-serif')}`;
      draftWidth = Math.max(...textarea.value.split('\n').map(line => context.measureText(line).width));
      context.restore();
    }
    const width = Math.max(20, Math.min(maxWidth, Math.ceil(Math.max(bounds.right - bounds.left, draftWidth)) + 2));
    const lineHeight = text._display._textInfo?.[0]?.height || 16;
    const height = Math.ceil(Math.max(lineHeight, bounds.bottom - bounds.top)) + 2;
    text.set('opacity', 0);
    // Explicit HTML container dimensions avoid amCharts' per-frame DOM
    // measurement and make the editor share the canvas text's coordinates.
    input.setAll({ width, height, x: text.x() + bounds.left, y: text.y() + bounds.top });
    textarea.rows = 1;
    textarea.wrap = panel ? 'soft' : 'off';
    const values = {
      boxSizing: 'border-box', display: 'block', margin: '0px', padding: '0px', border: '0px',
      outline: 'none', boxShadow: 'none', resize: 'none', backgroundColor: 'transparent',
      // Editing uses the panel foreground, even for white drawing ink. The
      // selected drawing color remains unchanged on the saved canvas label.
      color: 'var(--content-primary, #e5e7eb)',
      fontFamily: label.get('fontFamily') || 'sans-serif', fontSize: typeof label.get('fontSize') === 'number' ? `${label.get('fontSize')}px` : label.get('fontSize', '13px'),
      fontWeight: String(label.get('fontWeight', 'normal')), fontStyle: label.get('fontStyle', 'normal'),
      lineHeight: `${lineHeight}px`, textAlign: label.get('textAlign', 'start'),
      whiteSpace: panel ? 'pre-wrap' : 'pre', overflowWrap: 'anywhere',
      width: `${width}px`, height: `${height}px`, minWidth: '0px', minHeight: '0px',
      maxWidth: 'none', maxHeight: 'none', overflow: 'hidden',
    };
    Object.entries(values).forEach(([key, value]) => style(textarea, key, value));
    // Inline important isolates the drawing from global form focus styling.
    textarea.style.setProperty('color', values.color, 'important');
    textarea.style.setProperty('box-shadow', 'none', 'important');
    style(host, 'boxShadow', '0 0 0 1px var(--content-secondary, #8994ab)');
    style(host, 'borderRadius', '2px');
    style(host, 'backgroundColor', 'transparent');
    style(host, 'translate', '0px 0px');
    style(host, 'overflow', 'hidden');
    style(host, 'pointerEvents', 'auto');
  }

  label._syncStyle = () => {
    if (label.get('active')) { syncEditor(); schedule(); }
  };
  listeners.push(label.events.on('boundschanged', schedule));
  listeners.push(label.events.on('positionchanged', schedule));
  listeners.push(label.on('active', active => {
    if (!active) {
      let ancestor = label.parent;
      while (ancestor && !ancestor.isType('StockChart')) ancestor = ancestor.parent;
      const resizer = ancestor?.spriteResizer;
      if (resizer?.get('sprite') === label) resizer.set('sprite', undefined);
    }
    schedule();
  }));
  label.addDisposer({ dispose() {
    disposed = true;
    cancelAnimationFrame(pending);
    listeners.forEach(listener => listener.dispose());
  } });
}
